import type {
  CurriculumWithFullCoursesResponse,
  FullCurriculumCourse,
} from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { useToast } from '@lms/ui/use-toast';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useState } from 'react';
import { updateCourseOrder } from '@/features/curriculums/curriculums-api';

// Ports app/[brand]/admin/curriculums/_components/order-buttons.tsx. The Next
// version moved a course by a relative direction (-1 left/up, +1 right/down) and
// relied on `revalidatePath`; here we call the backend then let the caller
// revalidate its SWR cache via `onMoved`.
export function OrderButtons({
  curriculum,
  course,
  onMoved,
}: {
  curriculum: CurriculumWithFullCoursesResponse;
  course: FullCurriculumCourse;
  onMoved: () => void;
}) {
  const { toast } = useToast();
  const [pending, setPending] = useState(false);

  const move = async (direction: number) => {
    setPending(true);
    try {
      const result = await updateCourseOrder(
        curriculum.id,
        course.id,
        direction,
      );
      if (result.ok) {
        onMoved();
      } else {
        toast({ variant: 'destructive', description: result.error });
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mt-2 grid grid-cols-2 gap-2">
      <Button
        variant="outline"
        disabled={pending || (course.order ?? 1) <= 1}
        onClick={() => move(-1)}
      >
        <ArrowLeftIcon />
      </Button>
      <Button
        variant="outline"
        disabled={pending || (course.order ?? 1) >= curriculum.courses.length}
        onClick={() => move(1)}
      >
        <ArrowRightIcon />
      </Button>
    </div>
  );
}
